import { searchContent } from '@echo/api-client/onearly';
import type { StrongerMemoryTypes } from '../config/environment';
import { authorOf, type ContentGraph, jsonValueOf, readGraph } from './graph';
import { type SmSummary, smSummaryValueSchema } from './schemas';

/**
 * Home › "Group progress": one row per member of the global group, from
 * the SM Progress Summary each of them shares with the group's members
 * (docs/mvp/03-CONTENT.md). A member appears once they have published a summary for the
 * week — the app does that on Home and after every exercise — so nobody is invented and
 * nobody's sessions are read.
 */
export interface MemberProgress {
  actorUuid: string;
  /** Display name as Core hydrates it ("Jane Doe"); empty when the profile is not readable. */
  name: string;
  firstName: string;
  lastName: string;
  photoUrl: string | undefined;
  done: number;
  target: number;
  /** Days of the week on which this member completed the whole routine. */
  daysDone: string[];
  currentStreak: number;
  bestStreak: number;
  sessionsThisMonth: number;
  /** Days the routine was ever completed; undefined for summaries written before it existed. */
  sessionsTotal: number | undefined;
  /** Exercises completed, ever, per kind. */
  completionByKind: { read: number; write: number; math: number };
  /** ISO time of the summary used, to keep the newest one per member. */
  asOf: string;
}

export { weekStartOf } from './calendar';

/** Completed dates that fall in the week starting on `weekStart`. */
export function daysDoneInWeek(completedDates: readonly string[], weekStart: string): string[] {
  const end = new Date(`${weekStart}T00:00:00Z`);
  end.setUTCDate(end.getUTCDate() + 6);
  const weekEnd = end.toISOString().slice(0, 10);
  return completedDates.filter((date) => date >= weekStart && date <= weekEnd).sort();
}

export async function loadGroupProgress(
  types: StrongerMemoryTypes,
  weekStart: string,
): Promise<MemberProgress[]> {
  const response = await searchContent({
    rootContentTypeUuids: [types.summary],
    maxDepth: 0,
    first: 100,
  });
  if (response.status !== 200)
    throw new Error(`OnEarly: group progress search failed (${response.status})`);
  return groupProgressFromGraph(readGraph(response.data), types, weekStart);
}

/**
 * One entry per author for `weekStart`, keeping the newest summary (a person writes a new
 * node after every exercise; Core has no versioning through REST). Summaries without the
 * week fields — written before this shape existed — are ignored.
 */
export function groupProgressFromGraph(
  graph: ContentGraph,
  types: StrongerMemoryTypes,
  weekStart: string,
): MemberProgress[] {
  const byMember = new Map<string, MemberProgress>();
  for (const node of graph.nodes.values()) {
    if (node.contentTypeUuid !== types.summary) continue;
    const parsed = smSummaryValueSchema.safeParse(jsonValueOf(node));
    if (!parsed.success || parsed.data.weekStart !== weekStart) continue;
    const author = authorOf(graph, node);
    if (!author) continue;
    const entry = toMemberProgress(author.uuid, author, parsed.data, node.createdAt ?? '');
    const current = byMember.get(author.uuid);
    if (!current || entry.asOf > current.asOf) byMember.set(author.uuid, entry);
  }
  return [...byMember.values()];
}

function toMemberProgress(
  actorUuid: string,
  author: {
    name?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    profileImage?: { small?: { url?: string | null } | null } | null;
  },
  summary: SmSummary,
  createdAt: string,
): MemberProgress {
  const firstName = author.firstName?.trim() ?? '';
  const lastName = author.lastName?.trim() ?? '';
  const name = author.name?.trim() || `${firstName} ${lastName}`.trim();
  return {
    actorUuid,
    name,
    firstName: firstName || name.split(' ')[0] || '',
    lastName: lastName || name.split(' ').slice(1).join(' '),
    photoUrl: author.profileImage?.small?.url ?? undefined,
    done: Math.min(summary.weekDaysDone.length, summary.weekTarget),
    target: summary.weekTarget,
    daysDone: [...summary.weekDaysDone].sort(),
    currentStreak: summary.currentStreak,
    bestStreak: summary.bestStreak,
    sessionsThisMonth: summary.sessionsThisMonth,
    sessionsTotal: summary.sessionsTotal,
    completionByKind: {
      read: summary.completionByKind.read ?? 0,
      write: summary.completionByKind.write ?? 0,
      math: summary.completionByKind.math ?? 0,
    },
    asOf: createdAt || summary.asOf,
  };
}

/**
 * The member detail's weekly chart: how many routine days were complete by
 * the end of each day, Monday → Sunday, so the line climbs from 0 towards the week target.
 */
export function cumulativeDaysByWeekday(daysDone: readonly string[], weekStart: string): number[] {
  const done = new Set(daysDone);
  const values: number[] = [];
  let total = 0;
  const cursor = new Date(`${weekStart}T00:00:00Z`);
  for (let index = 0; index < 7; index += 1) {
    if (done.has(cursor.toISOString().slice(0, 10))) total += 1;
    values.push(total);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return values;
}

/** The signed-in person first, then everyone else by name — the Figma order. */
export function orderMembers(
  members: readonly MemberProgress[],
  meUuid: string | undefined,
): MemberProgress[] {
  return [...members].sort((a, b) => {
    if (a.actorUuid === meUuid) return -1;
    if (b.actorUuid === meUuid) return 1;
    return a.name.localeCompare(b.name);
  });
}

/** Members who completed the whole routine on `date` — the group card's "n of m have completed today". */
export function membersDoneOn(members: readonly MemberProgress[], date: string): MemberProgress[] {
  return members.filter((member) => member.daysDone.includes(date));
}
